import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const UserForm = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [taxId, setTaxId] = useState('');
    const [address, setAddress] = useState('');
    const [bankAccountNumber, setBankAccountNumber] = useState('');
    const [parentUserId, setParentUserId] = useState('');
    const [users, setUsers] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [nipError, setNipError] = useState('');
    const [editUserId, setEditUserId] = useState(null);
    const { username: editUsername } = useParams();
    const navigate = useNavigate();

    // Walidacja NIP - polska suma kontrolna
    const validateNIP = (nip) => {
        // Usuń myślniki i spacje
        const cleanNip = nip.replace(/[-\s]/g, '');

        if (!/^\d{10}$/.test(cleanNip)) {
            return { valid: false, message: 'NIP musi zawierać dokładnie 10 cyfr' };
        }

        const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
        let sum = 0;

        for (let i = 0; i < 9; i++) {
            sum += parseInt(cleanNip[i]) * weights[i];
        }

        const checksum = sum % 11;
        const lastDigit = parseInt(cleanNip[9]);

        if (checksum !== lastDigit) {
            return { valid: false, message: 'Nieprawidłowa suma kontrolna NIP' };
        }

        return { valid: true, message: '' };
    };

    const handleNipChange = (value) => {
        setTaxId(value);
        if (value.replace(/[-\s]/g, '').length === 10) {
            const validation = validateNIP(value);
            setNipError(validation.valid ? '' : validation.message);
        } else {
            setNipError('');
        }
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await api.get('/users/');
                const allUsers = response.data.filter(u => u.role === 'user');
                setUsers(allUsers);

                // Przy edycji — znajdź użytkownika na liście i wypełnij formularz
                if (editUsername) {
                    const userData = allUsers.find(u => u.username === editUsername);
                    if (userData) {
                        setEditUserId(userData.id);
                        setUsername(userData.username);
                        setFullName(userData.full_name || '');
                        setTaxId(userData.tax_id_number || '');
                        setAddress(userData.address || '');
                        setBankAccountNumber(userData.bank_account_number || '');
                        setParentUserId(userData.parent_user_id ? String(userData.parent_user_id) : '');
                    }
                }
            } catch (error) {
                console.error("Failed to fetch users", error);
            }
        };
        fetchUsers();
    }, [editUsername]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        // Walidacja wymaganych pól
        if (!fullName.trim()) {
            setErrorMessage('Nazwa pełna jest wymagana');
            return;
        }
        if (!address.trim()) {
            setErrorMessage('Adres jest wymagany');
            return;
        }

        const nipValidation = validateNIP(taxId);
        if (!nipValidation.valid) {
            setErrorMessage(nipValidation.message);
            return;
        }

        try {
            if (editUsername) {
                // Aktualizacja użytkownika – nie edytujemy hasła ani roli
                await api.put(`/users/${editUserId}`, {
                    full_name: fullName,
                    tax_id_number: taxId.replace(/[-\s]/g, ''),
                    address: address,
                    bank_account_number: bankAccountNumber || null,
                    parent_user_id: parentUserId || null,
                });
            } else {
                // Tworzenie nowego użytkownika – rola ustawiana jest domyślnie na "user"
                await api.post('/users/', {
                    username,
                    password,
                    full_name: fullName,
                    tax_id_number: taxId.replace(/[-\s]/g, ''),
                    address,
                    bank_account_number: bankAccountNumber || null,
                    parent_user_id: parentUserId || null,
                    role: 'user',
                });
            }
            navigate('/admin');
        } catch (error) {
            console.error("Error saving user", error);
            const detail = error.response?.data?.detail;
            if (typeof detail === 'string') {
                setErrorMessage(detail);
            } else if (Array.isArray(detail)) {
                setErrorMessage(detail.map(e => e.msg).join(', '));
            } else {
                setErrorMessage('Wystąpił błąd podczas zapisywania użytkownika');
            }
        }
    };

    // Filtruj użytkowników - nie można wybrać siebie jako rodzica
    const availableParentUsers = users.filter(u => u.username !== editUsername);

    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
                <h2 className="text-2xl font-bold mb-6">
                    {editUsername ? 'Edytuj użytkownika' : 'Dodaj użytkownika'}
                </h2>

                {errorMessage && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                        {errorMessage}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">Nazwa użytkownika:</label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            disabled={!!editUsername}
                            required
                        />
                    </div>

                    {!editUsername && (
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2">Hasło:</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                required
                                minLength="8"
                                placeholder="Wprowadź hasło (min. 8 znaków)"
                            />
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">
                            Pełna nazwa: <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            placeholder="Nazwa firmy lub imię i nazwisko"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">
                            NIP: <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={taxId}
                            onChange={(e) => handleNipChange(e.target.value)}
                            className={`w-full p-2 border rounded-lg ${nipError ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder="Wprowadź NIP (10 cyfr)"
                            required
                        />
                        {nipError && (
                            <p className="text-red-500 text-sm mt-1">{nipError}</p>
                        )}
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">
                            Adres: <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            placeholder="Wprowadź adres"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">Numer konta bankowego:</label>
                        <input
                            type="text"
                            value={bankAccountNumber}
                            onChange={(e) => setBankAccountNumber(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            placeholder="Wprowadź numer konta bankowego"
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-gray-700 mb-2">Podmiot nadrzędny:</label>
                        <select
                            value={parentUserId}
                            onChange={(e) => setParentUserId(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                        >
                            <option value="">-- Brak --</option>
                            {availableParentUsers.map(user => (
                                <option key={user.id} value={String(user.id)}>
                                    {user.full_name || user.username}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/admin')}
                            className="flex-1 bg-gray-300 text-gray-700 p-2 rounded-lg hover:bg-gray-400 transition"
                        >
                            Anuluj
                        </button>
                        <button
                            type="submit"
                            className="flex-1 bg-softTeal text-white p-2 rounded-lg hover:bg-warmOrange transition"
                        >
                            {editUsername ? 'Zaktualizuj użytkownika' : 'Utwórz użytkownika'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserForm;
